import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { GoPrimitiveDot } from "react-icons/go";
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import { Row, Column } from "../../../components/Grid/Grid.component";
import Form from "./SportCategory.form.component";
import Button from "../../../components/Button/Button.component";
import Error from "../../../components/Error/ErrorMessage.component";
import BorderSelect from "../../../components/Form/BorderSelect/BorderSelect.component";
import Notification from "../../../components/Toast/Toast.component";

// GQL
import {
  UPDATE_SPORT_CATEGORY,
  DELETE_SPORT_CATEGORY,
} from "../../../graphql/mutations/sportCategory.mutation";
import { GET_SPORT_CATEGORY } from "../../../graphql/queries/sportCategory.query";

import { formatGraphqlError } from "../../../utils/api";

const SportCategoryEdit = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const [sportCategoryData, setSportCategoryData] = useState({
    id: "",
    title: "",
    description: "",
    status: "ACTIVE",
  });
  const [updateSportCategory, { loading }] = useMutation(UPDATE_SPORT_CATEGORY);
  const [deleteSportCategory] = useMutation(DELETE_SPORT_CATEGORY);
  const { id } = useParams();
  const sportCategory = useQuery(GET_SPORT_CATEGORY, {
    fetchPolicy: "no-cache",
    variables: { id },
  });
  const { data } = sportCategory;
  const history = useHistory();

  useEffect(() => {
    setActiveSideNav("sysConf");
    setPageTitle("Manage Sport Category");
  });

  const { register, handleSubmit, errors, setValue, getValues } = useForm();

  const changeStatus = (e) => {
    const tempSportCategoryData = { ...sportCategoryData };
    tempSportCategoryData.status = e.target.value;
    setSportCategoryData(tempSportCategoryData);
  };

  const getColor = () => {
    switch (sportCategoryData.status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const onSubmit = (values) => {
    /**
     * Create new Sport Category
     * push to list all sports
     */

    const submitValues = values;
    submitValues.id = sportCategoryData.id;
    updateSportCategory({
      variables: {
        ...submitValues,
      },
    })
      .then(() => {
        Notification.success({
          title: "Saved successfully",
          message: "You updated a sport type",
        });
        history.push("/app/system-configuration/sport-category");
      })
      .catch((err) =>
        Notification.error({
          title: "Create Sport Category Failed",
          message: formatGraphqlError(err),
        })
      );
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteSportCategory({
          variables: {
            id: sportCategoryData.id,
          },
        })
          .then(() => {
            Swal.fire(
              "Deleted!",
              "Sport Category has been deleted.",
              "success"
            ).then(() => {
              history.push("/app/system-configuration/sport-category");
            });
          })
          .catch((err) =>
            Notification.error({
              title: "Delete Sport Category Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  if (sportCategory.loading) {
    return "Loading...";
  }

  return (
    <>
      <PageHeader title={`Edit Sport Category: ${sportCategoryData.title}`} back />
      <Row>
        <Column width="70%" noPad>
          <Form
            register={register}
            errors={errors}
            sportCategoryData={sportCategoryData}
            setSportCategoryData={setSportCategoryData}
            sportCategoryEditData={data && data.sportCategory}
            setValue={setValue}
            getValues={getValues}
          />
        </Column>
        <Column width="30%">
          <BorderSelect
            header="Status"
            name="status"
            label="Please select a status"
            value={sportCategoryData.status}
            register={register}
            onChange={changeStatus}
            options={[
              { value: "ACTIVE", text: "Active" },
              { value: "ARCHIVE", text: "Archive" },
              { value: "DRAFT", text: "Draft" },
            ]}
            icon={
              <GoPrimitiveDot style={{ color: getColor(), fontSize: "25px" }} />
            }
            margin="60px 0px 120px 0px"
            required={{
              required: "please enter a Sport Category ID",
              pattern: {
                value: /^[a-zA-Z]+$/,
                message: "field cannot be empty",
              },
            }}
            error={errors.id && <Error message={errors.id.message} />}
          />

          <Button
            width="100%"
            onClick={() => handleDelete()}
            type="button"
            value="Archive"
            loading={loading}
          />
          <Button
            width="100%"
            onClick={handleSubmit(onSubmit)}
            type="button"
            kind="secondary"
            value="Save & Update"
            loading={loading}
          />
        </Column>
      </Row>
    </>
  );
};

export default SportCategoryEdit;
